import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useOnboarding } from '../../../components/redux/hooks/useOnboarding';
import { fetchSessionDetails } from '../../../components/redux/slices/onboardingSlice';
import { usePostHog, POSTHOG_EVENTS } from '../../../components/analytics/PostHogProvider';
import api from '../../../services/api';

import ConnectEHRModal from '../modals/ConnectEHRModal';

// Toggle this to disable mock data and use real session data
const USE_MOCK_DATA = true;

// Mock dashboard data structure
const MOCK_DASHBOARD_DATA = {
  quick_stats: {
    tasks_completed: 0,
    total_tasks: 4,
    next_milestone: '25%'
  },
  tasks: {
    connect_ehr: {
      time: 5,
      difficulty: 'easy',
      xp_point: 50,
      locked: false
    },
    configure_phone_tree: {
      time: 10,
      difficulty: 'medium',
      xp_point: 75,
      locked: true
    },
    provider_data: {
      time: 15,
      difficulty: 'medium',
      xp_point: 100,
      locked: true
    },
    test_call_flow: {
      time: 5,
      difficulty: 'easy',
      xp_point: 50,
      locked: true
    }
  },
  achievement: {
    total_xp: 0,
    remain_xp_next_level: 80,
    recent_badges: [
      { id: 1, name: 'Getting Started', icon: 'star' },
      { id: 2, name: 'Team Player', icon: 'users' },
      { id: 3, name: 'Locked Badge', icon: 'lock' }
    ],
    streak_days: 3
  }
};

const Dashboard = () => {
  const [isEHRModalOpen, setIsEHRModalOpen] = useState(false);
  const [isSavingMockData, setIsSavingMockData] = useState(false);
  const { captureEvent } = usePostHog();
  const dispatch = useDispatch();
  const hasFetchedRef = useRef(false);
  const mockDataSavedRef = useRef(false);
  
  // Get session and dashboard state from Redux
  const {
    sessionId,
    sessionData,
    isFetchingSession,
    fetchSessionError,
    setDashboardLoadingState,
    setDashboardDataState,
    setDashboardErrorState,
    clearDashboardErrorState,
    resetDashboard,
  } = useOnboarding();
  
  // Get dashboard data from sessionData.additional_data.dashboard_data
  const dashboardData = sessionData?.additional_data?.dashboard_data || null;
  const isLoadingDashboard = isFetchingSession || isSavingMockData;
  const dashboardError = fetchSessionError;
  
  // Track dashboard view
  useEffect(() => {
    captureEvent(POSTHOG_EVENTS.DASHBOARD_VIEWED, { sessionId });
  }, [captureEvent, sessionId]);
  
  /**
   * Save mock dashboard data to session API
   * This function can be disabled by setting USE_MOCK_DATA to false
   */
  const saveMockDashboardData = useCallback(async (sessId) => {
    if (!USE_MOCK_DATA) {
      console.log('Mock data disabled, skipping...');
      return false;
    }
    
    setIsSavingMockData(true);
    try {
      console.log('Saving mock dashboard data to session:', sessId);
      await api.patch(`/onboarding/sessions/${sessId}/`, {
        additional_data: {
          dashboard_data: MOCK_DASHBOARD_DATA
        }
      });
      console.log('Mock dashboard data saved successfully');
      return true;
    } catch (error) {
      console.error('Failed to save mock dashboard data:', error);
      return false;
    } finally {
      setIsSavingMockData(false);
    }
  }, []);
  
  /**
   * Fetch session data from API and store in Redux
   */
  const fetchDashboardFromSession = useCallback(async (sessId) => {
    console.log('Fetching session data for dashboard, session ID:', sessId);
    dispatch(fetchSessionDetails(sessId));
  }, [dispatch]);
  
  // Initialize dashboard data on component mount
  useEffect(() => {
    const initializeDashboard = async () => {
      if (!sessionId || hasFetchedRef.current) return;
      
      hasFetchedRef.current = true;
      
      // If USE_MOCK_DATA is enabled and we haven't saved mock data yet
      if (USE_MOCK_DATA && !mockDataSavedRef.current && !dashboardData) {
        mockDataSavedRef.current = true;
        const saved = await saveMockDashboardData(sessionId);
        if (saved) {
          // Fetch the updated session data
          await fetchDashboardFromSession(sessionId);
        }
      } else {
        // Just fetch session data (real data mode)
        await fetchDashboardFromSession(sessionId);
      }
    };
    
    initializeDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  
  // Transform API data to component format
  const transformDashboardData = (data) => {
    if (!data) return null;
    
    // Calculate progress percentage based on tasks completed
    const progressPercentage = data.quick_stats ? 
      Math.round((data.quick_stats.tasks_completed / data.quick_stats.total_tasks) * 100) : 0;
    
    // Transform tasks data
    const tasksArray = data.tasks ? Object.entries(data.tasks).map(([key, task], index) => ({
      id: index + 1,
      key: key,
      title: formatTaskTitle(key),
      description: formatTaskDescription(key),
      duration: `${task.time} min`,
      difficulty: task.difficulty.charAt(0).toUpperCase() + task.difficulty.slice(1),
      xp: parseInt(task.xp_point),
      status: task.locked ? 'locked' : 'available',
      locked: task.locked
    })) : [];
    
    // Transform achievements data
    const achievementsData = data.achievement ? {
      totalXP: data.achievement.total_xp,
      xpToNextLevel: data.achievement.remain_xp_next_level,
      badges: data.achievement.recent_badges.map((badge, index) => ({
        id: badge.id || index + 1,
        name: badge.name,
        icon: badge.icon === 'star' ? '📊' : badge.icon === 'users' ? '👥' : '🔒',
        unlocked: true
      })),
      streakDays: 3 // Default value since not provided by API
    } : {
      totalXP: 0,
      xpToNextLevel: 80,
      badges: [],
      streakDays: 3
    };
    
    return {
      progress: {
        percentage: progressPercentage,
        tasksCompleted: data.quick_stats?.tasks_completed || 0,
        totalTasks: data.quick_stats?.total_tasks || 8,
        timeInvested: 25, // Default value since not provided by API
        nextMilestone: data.quick_stats?.next_milestone?.replace('%', '') || 25
      },
      tasks: tasksArray,
      achievements: achievementsData
    };
  };
  
  // Helper functions to format task titles and descriptions
  const formatTaskTitle = (key) => {
    const titles = {
      'connect_ehr': 'Connect EHR',
      'configure_phone_tree': 'Configure Phone Tree',
      'provider_data': 'Provider Data',
      'test_call_flow': 'Test Call Flow'
    };
    return titles[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  const formatTaskDescription = (key) => {
    const descriptions = {
      'connect_ehr': 'Link your Electronic Health Records system',
      'configure_phone_tree': 'Set up your automated call routing',
      'provider_data': 'Upload staff roster and schedules',
      'test_call_flow': 'Verify your automated call handling'
    };
    return descriptions[key] || 'Complete this task to continue setup';
  };
  
  const transformedData = transformDashboardData(dashboardData);
  
  // Log when dashboard data is loaded
  useEffect(() => {
    if (dashboardData) {
      console.log('Dashboard data loaded successfully:', dashboardData);
      console.log('Transformed data:', transformedData);
    }
  }, [dashboardData]);
  
  const progress = useMemo(() => transformedData?.progress || {
    percentage: 0,
    tasksCompleted: 0,
    totalTasks: 8,
    timeInvested: 25,
    nextMilestone: 25
  }, [transformedData]);
  
  const tasks = useMemo(() => transformedData?.tasks || [], [transformedData]);
  
  const achievements = useMemo(() => transformedData?.achievements || {
    totalXP: 0,
    xpToNextLevel: 80,
    badges: [],
    streakDays: 3
  }, [transformedData]);

  const [showAllTasks, setShowAllTasks] = useState(false);
  const [newTaskIds, setNewTaskIds] = useState(new Set());

  // Track new tasks when data changes
  useEffect(() => {
    if (dashboardData?.tasks) {
      const currentTaskIds = new Set(Object.keys(dashboardData.tasks).map((_, index) => index + 1));
      setNewTaskIds(currentTaskIds);
      
      // Remove animation class after animation completes
      const timer = setTimeout(() => {
        setNewTaskIds(new Set());
      }, 400); // Match animation duration
      
      return () => clearTimeout(timer);
    }
  }, [dashboardData?.tasks]);

  const handleStartTask = (taskId) => {
    console.log('Starting task:', taskId);
    // Open modal for Connect EHR task (id: 1)
    if (taskId === 1) {
      setIsEHRModalOpen(true);
    } else {
      // Navigate to specific task or open other modals
      console.log('Task not yet implemented:', taskId);
    }
  };

  const handleViewAllTasks = () => {
    setShowAllTasks(!showAllTasks);
  };

  const handleChatWithOps = () => {
    console.log('Chat with Ops');
  };

  const handleBookHelp = () => {
    console.log('Book 15-min help');
  };

  const handleRequestAssisted = () => {
    console.log('Request assisted onboarding');
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'text-white';
      case 'Medium':
        return 'text-white';
      case 'Hard':
        return 'text-white';
      default:
        return '';
    }
  };

  const getDifficultyBackground = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return '#d1fae5';
      case 'Medium':
        return '#fef3c7';
      case 'Hard':
        return '#fee2e2';
      default:
        return '#e5e7eb';
    }
  };

  const getDifficultyTextColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return '#065f46';
      case 'Medium':
        return '#92400e';
      case 'Hard':
        return '#991b1b';
      default:
        return '#6b7280';
    }
  };

  return (
    <div className="min-vh-content" style={{ minHeight: 'calc(100vh - 64px)', background: 'linear-gradient(to bottom, #f0f4ff 0%, #ffffff 100%)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif', width: '100%' }}>
      {/* Loading State */}
      {isLoadingDashboard && (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 400 }}>
          <div className="loading-spinner"></div>
          <p className="mt-3 text-muted">Loading dashboard data...</p>
        </div>
      )}
      
      {/* Error State */}
      {dashboardError && (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 400 }}>
          <div className="text-center">
            <h3 className="h4 text-danger mb-2">Failed to load dashboard</h3>
            <p className="text-muted mb-3">{dashboardError.error || 'Please try again later.'}</p>
            <button 
              className="btn btn-primary"
              onClick={() => {
                console.log('Retrying dashboard data fetch for session ID:', sessionId);
                hasFetchedRef.current = false;
                if (sessionId) {
                  fetchDashboardFromSession(sessionId);
                }
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      {!isLoadingDashboard && !dashboardError && (
        <div className="mx-auto" style={{ maxWidth: 1400, padding: '2rem 2rem' }}>
        {/* Welcome Section */}
        <div className="mb-4" style={{ marginBottom: '2rem', background: 'transparent', padding: '1.5rem 0', borderRadius: 0 }}>
          <h1 className="h2 fw-bold text-dark mb-2" style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 0.5rem 0' }}>Welcome back, Dr. Smith!</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.9375rem', color: '#6b7280', margin: 0 }}>You&apos;re making great progress. Let&apos;s continue setting up your clinic.</p>
        </div>

        {/* Main Grid */}
        <div className="row g-4" style={{ display: 'grid', gridTemplateColumns: '380px 1fr 380px', gap: '2rem' }}>
          {/* Left Column - Progress */}
          <div className="d-flex flex-column gap-4" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Circular Progress */}
            <div className="card border-0 shadow-sm rounded-3" style={{ background: 'white', borderRadius: 12, padding: '2rem', border: '1px solid #f3f4f6' }}>
              <div className="position-relative mx-auto mb-4" style={{ position: 'relative', width: 200, height: 200, margin: '0 auto 1.5rem' }}>
                <svg className="position-absolute top-0 start-0" style={{ transform: 'rotate(-90deg)' }} width="200" height="200">
                  <circle
                    stroke="#e5e7eb"
                    strokeWidth="16"
                    fill="transparent"
                    r="84"
                    cx="100"
                    cy="100"
                  />
                  <circle
                    className="transition-all duration-500 ease-out"
                    stroke="#3b82f6"
                    strokeWidth="16"
                    fill="transparent"
                    r="84"
                    cx="100"
                    cy="100"
                    strokeDasharray={`${2 * Math.PI * 84}`}
                    strokeDashoffset={`${2 * Math.PI * 84 * (1 - progress.percentage / 100)}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                  <div className="h1 fw-bold text-dark mb-1" style={{ fontSize: '3rem', fontWeight: 700, color: '#111827', lineHeight: 1 }}>{progress.percentage}%</div>
                  <div className="small text-muted" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>Complete</div>
                </div>
              </div>
              <div className="text-center">
                <p className="d-flex align-items-center justify-content-center gap-2 mb-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500 }}>
                  <span style={{ fontSize: '1rem' }}>⏱️</span>
                  {8 - progress.tasksCompleted} tasks left until Mira can answer calls
                </p>
                <p className="small text-muted mb-0" style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>~15 minutes remaining</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="card border-0 shadow-sm rounded-3" style={{ background: 'white', borderRadius: 12, padding: '1.5rem', border: '1px solid #f3f4f6' }}>
              <h3 className="h5 fw-semibold text-dark mb-3" style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: '0 0 1rem 0' }}>Quick Stats</h3>
              <div className="d-flex justify-content-between align-items-center pb-2 border-bottom" style={{ borderBottom: '1px solid #f3f4f6' }}>
                <span className="small text-muted" style={{ fontSize: '0.875rem', color: '#6b7280' }}>Tasks Completed</span>
                <span className="small fw-semibold text-dark" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>{progress.tasksCompleted} / {progress.totalTasks}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center pb-2 border-bottom" style={{ borderBottom: '1px solid #f3f4f6' }}>
                <span className="small text-muted" style={{ fontSize: '0.875rem', color: '#6b7280' }}>Time Invested</span>
                <span className="small fw-semibold text-dark" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>{progress.timeInvested} min</span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="small text-muted" style={{ fontSize: '0.875rem', color: '#6b7280' }}>Next Milestone</span>
                <span className="small fw-semibold" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#3b82f6' }}>{progress.nextMilestone}%</span>
              </div>
            </div>
          </div>

          {/* Middle Column - Tasks */}
          <div className="d-flex flex-column">
            <div className="card border-0 shadow-sm rounded-3" style={{ background: 'white', borderRadius: 12, padding: '1.5rem', border: '1px solid #f3f4f6', height: 'fit-content' }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="h5 fw-semibold text-dark mb-0" style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>Your Tasks</h3>
                <button 
                  className="btn btn-link text-primary p-1" 
                  style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '0.875rem', cursor: 'pointer', padding: '0.25rem 0.5rem' }}
                  onClick={handleViewAllTasks}
                >
                  {showAllTasks ? 'Show Less' : 'View All'}
                </button>
              </div>
              <div className="d-flex flex-column gap-3">
                {(showAllTasks ? tasks : tasks.slice(0, 2)).map((task) => (
                  <div key={task.id} className={`d-flex justify-content-between align-items-center p-3 border rounded-3 ${task.locked ? 'opacity-75 bg-light' : ''} ${newTaskIds.has(task.id) ? 'animate-fade-in' : ''}`} style={{ border: '1px solid #e5e7eb', borderRadius: 8, transition: 'all 0.2s' }}>
                    <div className="flex-grow-1">
                      <div className="mb-2" style={{ marginBottom: '0.75rem' }}>
                        <h4 className="h6 fw-semibold text-dark mb-1" style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', margin: '0 0 0.25rem 0' }}>{task.title}</h4>
                        <p className="small text-muted mb-0" style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>{task.description}</p>
                      </div>
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <span className="d-flex align-items-center gap-1 small fw-medium" style={{ color: '#6366f1', display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 500 }}>
                          <span style={{ fontSize: '0.875rem' }}>⏱️</span>
                          {task.duration}
                        </span>
                        <span className="badge" style={{ 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: 6, 
                          fontWeight: 600,
                          background: getDifficultyBackground(task.difficulty),
                          color: getDifficultyTextColor(task.difficulty)
                        }}>
                          {task.difficulty}
                        </span>
                        <span className="d-flex align-items-center gap-1 small fw-medium" style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 500 }}>
                          <span style={{ fontSize: '0.875rem' }}>⭐</span>
                          {task.xp} XP
                        </span>
                      </div>
                    </div>
                    <div className="ms-3">
                      {task.locked ? (
                        <button className="btn btn-light d-flex align-items-center gap-2" disabled style={{ background: '#e5e7eb', color: '#9ca3af', border: 'none', borderRadius: 6, padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.875rem' }}>🔒</span>
                          Locked
                        </button>
                      ) : (
                        <button 
                          className="btn btn-primary d-flex align-items-center"
                          style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                          onClick={() => handleStartTask(task.id)}
                        >
                          Start
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Achievements & Help */}
          <div className="d-flex flex-column gap-4" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Achievements Card */}
            <div className="card border-0 shadow-sm rounded-3" style={{ background: 'white', borderRadius: 12, padding: '1.5rem', border: '1px solid #f3f4f6' }}>
              <h3 className="h5 fw-semibold text-dark mb-4" style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: '0 0 1.5rem 0' }}>Your Achievements</h3>
              
              <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small text-muted" style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total XP</span>
                  <span className="h4 fw-bold" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#8b5cf6' }}>{achievements.totalXP}</span>
                </div>
                <div className="progress" style={{ height: 8, background: '#e5e7eb', borderRadius: 4, marginBottom: '0.5rem' }}>
                  <div 
                    className="progress-bar"
                    style={{ 
                      height: '100%', 
                      background: 'linear-gradient(to right, #8b5cf6, #a78bfa)', 
                      borderRadius: 4, 
                      width: `${(achievements.totalXP / (achievements.totalXP + achievements.xpToNextLevel)) * 100}%`,
                      transition: 'width 0.5s ease'
                    }}
                  ></div>
                </div>
                <p className="small text-muted mb-0" style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>{achievements.xpToNextLevel} XP to next level</p>
              </div>

              <div className="mb-4 pb-4 border-bottom" style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #f3f4f6' }}>
                <h4 className="h6 fw-semibold text-dark mb-3" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', margin: '0 0 0.75rem 0' }}>Recent Badges</h4>
                <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                  {achievements.badges.map((badge) => (
                    <div key={badge.id} className={`text-center p-2 rounded-2 ${!badge.unlocked ? 'opacity-50' : ''}`} style={{ borderRadius: 8 }}>
                      <div className="d-flex align-items-center justify-content-center rounded-circle mb-2" style={{ width: 32, height: 32, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                        {badge.unlocked ? badge.icon : '🔒'}
                      </div>
                      <div className="small text-center" style={{ fontSize: '0.75rem', lineHeight: 1.2 }}>{badge.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="h6 fw-semibold text-dark mb-3" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', margin: '0 0 0.75rem 0' }}>Streaks</h4>
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex gap-1">
                    {[...Array(3)].map((_, i) => (
                      <span key={i} style={{ fontSize: '1rem' }}>🔥</span>
                    ))}
                  </div>
                  <span className="small text-muted">{achievements.streakDays} days active</span>
                </div>
              </div>
            </div>

            {/* Help Card */}
            <div className="card border-0 shadow-sm rounded-3" style={{ background: 'white', borderRadius: 12, padding: '1.5rem', border: '1px solid #f3f4f6' }}>
              <h3 className="h5 fw-semibold text-dark mb-3" style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: '0 0 1rem 0' }}>Need Help?</h3>
              <button className="btn btn-link d-flex align-items-center justify-content-between w-100 p-2 text-start text-decoration-none" style={{ background: 'none', border: 'none', borderRadius: 6, padding: '0.75rem', cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleChatWithOps}>
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '1rem' }}>💬</span>
                  <span className="small fw-medium">Chat with Ops</span>
                </div>
                <span className="text-muted">›</span>
              </button>
              <button className="btn btn-link d-flex align-items-center justify-content-between w-100 p-2 text-start text-decoration-none" style={{ background: 'none', border: 'none', borderRadius: 6, padding: '0.75rem', cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleBookHelp}>
                <div className="d-flex align-items-center gap-2">
                  <span style={{ fontSize: '1rem' }}>📅</span>
                  <span className="small fw-medium">Book 15-min Help</span>
                </div>
                <span className="text-muted">›</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="mt-4" style={{ marginTop: '2rem' }}>
          <div className="card border-0 shadow-sm rounded-3" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 12, padding: '2rem' }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h3 className="h4 fw-bold text-white mb-2">Need us to finish this for you?</h3>
                <p className="text-white mb-0" style={{ opacity: 0.9 }}>Our onboarding specialists can handle the entire setup process</p>
              </div>
              <button 
                className="btn btn-light fw-semibold"
                style={{ background: 'white', color: '#667eea', border: 'none', borderRadius: 8, padding: '0.75rem 1.5rem', fontWeight: 600 }}
                onClick={handleRequestAssisted}
              >
                Request Assisted Onboarding
              </button>
            </div>
          </div>
        </div>
        </div>
      )}
      
      {/* Connect EHR Modal */}
      <ConnectEHRModal 
        isOpen={isEHRModalOpen} 
        onClose={() => setIsEHRModalOpen(false)} 
      />
    </div>
  );
};

export default Dashboard;
